import { useEffect } from "react";

interface SEOOptions {
  title: string;
  description: string;
  canonical?: string;
  jsonLd?: Record<string, any>;
}

/**
 * Lightweight SEO hook — sets <title>, meta description, canonical and optional JSON-LD
 * directly in the document head without external libs. Compatible with SPA + Googlebot.
 */
export function useSEO({ title, description, canonical, jsonLd }: SEOOptions) {
  useEffect(() => {
    // Title
    if (title) document.title = title;

    // Meta description
    let descTag = document.querySelector('meta[name="description"]');
    if (!descTag) {
      descTag = document.createElement("meta");
      descTag.setAttribute("name", "description");
      document.head.appendChild(descTag);
    }
    descTag.setAttribute("content", description);

    // OpenGraph title/description
    const setOg = (property: string, content: string) => {
      let tag = document.querySelector(`meta[property="${property}"]`);
      if (!tag) {
        tag = document.createElement("meta");
        tag.setAttribute("property", property);
        document.head.appendChild(tag);
      }
      tag.setAttribute("content", content);
    };
    setOg("og:title", title);
    setOg("og:description", description);
    setOg("og:type", "website");

    // Canonical
    if (canonical) {
      let canTag = document.querySelector('link[rel="canonical"]');
      if (!canTag) {
        canTag = document.createElement("link");
        canTag.setAttribute("rel", "canonical");
        document.head.appendChild(canTag);
      }
      canTag.setAttribute("href", canonical);
    }

    // JSON-LD structured data
    let scriptTag: HTMLScriptElement | null = null;
    if (jsonLd) {
      scriptTag = document.createElement("script");
      scriptTag.type = "application/ld+json";
      scriptTag.text = JSON.stringify(jsonLd);
      scriptTag.setAttribute("data-seo-jsonld", "true");
      document.head.appendChild(scriptTag);
    }

    return () => {
      // Clean up JSON-LD only (other tags are reused by next page)
      if (scriptTag && scriptTag.parentNode) {
        scriptTag.parentNode.removeChild(scriptTag);
      }
    };
  }, [title, description, canonical, jsonLd]);
}
